import { useState, useMemo, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import useTitanApp from "@/hooks/useTitanApp";
import { ConsultationButtonStatus } from "@/enum/ConsultationButtonStatus";
import {
  updateConsultationButtonStatus,
  getPatientConsultations,
  getConsultationAuditTimeline,
  getPatientActiveConsultation,
  summarizeConsultationSummaries,
  setSelectedConsultation,
  setSnomedCodes,
} from "@/dispatchers/patientDispatchers";
import { setStreamedText } from "@/dispatchers/templateDispatchers";
import { trackServiceBilling } from "@/dispatchers/patientDispatchers";
import { setConsultationLoadError } from "@/dispatchers/appDispatchers";
import React from "react";

interface ConsultationAction {
  actionID: string;
  actionName: string;
  actionNumber: string;
  payload: string | ActionPayload;
  isPerformed: boolean;
  image: string | null;
  actionStatus: number;
}

interface ActionPayload {
  vitalsDetails?: VitalsDetails;
  prescriptionDetails?: PrescriptionDetails;
  prescriptionsDetails?: PrescriptionItem[];
  vaccinationDetails?: VaccinationItem[];
  actionName?: string;
  actionNumber?: string;
  emailRecipients?: any[];
  includeEmailSignature?: boolean;
  emailCopyToPatient?: boolean;
  letterContent?: LetterContent;
  [key: string]: any; // For additional properties
}

interface VitalsDetails {
  patientId: string;
  patientName: string;
  consultationId: string;
  weight?: VitalMeasurement;
  height?: VitalMeasurement;
  bmi?: VitalMeasurement;
  heartRate?: VitalMeasurement;
  bloodPressure?: BloodPressure;
  temperature?: VitalMeasurement;
  respiratoryRate?: VitalMeasurement;
  circumferenceValue?: CircumferenceMeasurements;
  circumference?: CircumferenceSummary;
}

interface VitalMeasurement {
  Value: string;
  Unit: string;
}

interface BloodPressure {
  Systolic: VitalMeasurement;
  Diastolic: VitalMeasurement;
}

interface CircumferenceMeasurements {
  Unit: string;
  Waist?: string;
  Hip?: string;
  Neck?: string;
}

interface CircumferenceSummary {
  Unit: string;
  Value: string;
}

interface PrescriptionDetails {
  prescriptionId: string;
  patientId: string;
  pharmacyID: string;
  drugs: PrescriptionDrug[];
  base64Image?: string;
  prescriptionCategory: number;
}

interface PrescriptionItem {
  id?: number;
  value: string;
  prescribedItemId?: string;
  dose: string;
  quantity: string;
  notes?: string;
  schedule?: string;
  site?: string;
  manufacturer?: string;
  batchNumber?: string;
  expiryDate?: string;
}

interface PrescriptionDrug {
  prescriptionItemId: string;
  drugName: string;
  quantity: number;
  dose: string;
  note: string;
}

interface VaccinationItem {
  id?: number;
  value?: string;
  dose?: string;
  schedule?: string;
  site?: string;
  manufacturer?: string;
  batchNumber?: string;
  expiryDate?: string;
  vaccineName?: string;
  prescriptionItemId?: string;
  note?: string;
}

interface LetterContent {
  content: string;
}

// Action types based on your data
type ActionType =
  | "RecordVitals"
  | "PrescribeMedication"
  | "Vaccinations"
  | "SendReferralLetter"
  | "BookAppointment";

// Action categories for grouping in AI summary
type ActionCategory =
  | "Vitals Recording"
  | "Prescriptions"
  | "Vaccinations"
  | "Referrals"
  | "Appointments"
  | "Other Actions";

// Helper function to categorize actions
export const categorizeAction = (actionName: string): ActionCategory => {
  if (actionName?.includes("RecordVitals")) {
    return "Vitals Recording";
  } else if (actionName?.includes("PrescribeMedication")) {
    return "Prescriptions";
  } else if (actionName?.includes("Vaccinations")) {
    return "Vaccinations";
  } else if (actionName?.includes("SendReferralLetter")) {
    return "Referrals";
  } else if (actionName?.includes("BookAppointment")) {
    return "Appointments";
  } else {
    return "Other Actions";
  }
};

// Helper function to parse action payload
export const parseActionPayload = (
  payload: string | ActionPayload,
): ActionPayload => {
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch (error) {
      console.warn("Error parsing action payload:", error);
      return {};
    }
  }
  return payload;
};

// Helper function to extract medication names from prescription actions
export const extractMedicationNames = (
  action: ConsultationAction,
): string[] => {
  if (action.actionName?.includes("PrescribeMedication")) {
    const payload = parseActionPayload(action.payload);
    const drugs =
      payload.prescriptionsDetails ||
      [payload.prescriptionDetails?.drugs].flat().filter(Boolean);

    return drugs
      .map((drug: any) => drug.value || drug.drugName || "Unknown medication")
      .filter(Boolean);
  }
  return [];
};

// Helper function to extract vital measurements
export const extractVitalMeasurements = (
  action: ConsultationAction,
): VitalsDetails | null => {
  if (action.actionName?.includes("RecordVitals")) {
    const payload = parseActionPayload(action.payload);
    return payload.vitalsDetails || null;
  }
  return null;
};

// Type guard to check if action has vitals
export const isVitalsAction = (action: ConsultationAction): boolean => {
  return action.actionName?.includes("RecordVitals") ?? false;
};

// Type guard to check if action is prescription
export const isPrescriptionAction = (action: ConsultationAction): boolean => {
  return action.actionName?.includes("PrescribeMedication") ?? false;
};

// Type guard to check if action is vaccination
export const isVaccinationAction = (action: ConsultationAction): boolean => {
  return action.actionName?.includes("Vaccinations") ?? false;
};

// Type guard to check if action is referral
export const isReferralAction = (action: ConsultationAction): boolean => {
  return action.actionName?.includes("SendReferralLetter") ?? false;
};

// Type guard to check if action is appointment
export const isAppointmentAction = (action: ConsultationAction): boolean => {
  return action.actionName?.includes("BookAppointment") ?? false;
};

export interface Consultation {
  id: number;
  date: string;
  time: string;
  completedDate?: string;
  completedTime?: string;
  type: string;
  pharmacist: string;
  summary: string;
  actions: ConsultationAction[];
  serviceName?: string;
  serviceID?: string;
  consultationId?: string;
  consultationStatus?: number;
  patientId?: string;
  transcription?: string;
  patientConsent?: any;
  notes?: string;
  _id?: string;
  template?: any;
  templateId?: string;
  templateName?: string;
  shortSummary?: string;
  pharmacyID?: string;
  preConsultationTemplate?: any;
  createdByName?: string;
  createdBy?: string;
  createdOn?: string;
  completedOn?: string;
  consultationSummary?: string;
  consultationChat?: string;
  consultationButtonStatus?: number;
  companyID?: string;
}

export const actionTypes = [
  { value: "prescription", label: "Prescription", icon: "Pill" },
  { value: "payment", label: "Payment", icon: "CreditCard" },
  { value: "appointment", label: "Appointment", icon: "Calendar" },
  { value: "referral", label: "Referral", icon: "FileText" },
  { value: "vaccine", label: "Vaccine", icon: "Syringe" },
] as const;
export const defaultConsultations: Consultation[] = [];

export function mapPatientConsultationsToUI(
  patientConsultations: any[],
): Consultation[] {
  if (!patientConsultations) return [];
  return patientConsultations.map((c, index) => ({
    id: index + 1, // 👈 fallback (or use hash of _id)
    date: new Date(c.createdOn).toLocaleDateString("en-GB"), // e.g., 29/09/2025
    time: new Date(c.createdOn).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
    completedDate: c.completedOn
      ? new Date(c.completedOn).toLocaleDateString("en-GB")
      : null,

    completedTime: c.completedOn
      ? new Date(c.completedOn).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : null,
    type: c.templateName || "Consultation",
    pharmacist: c.completedByName || c.createdByName || "Unknown",
    summary: c.consultationSummary || "",
    actions: c?.actions?.length > 0 ? c?.actions : [],
    serviceName: c.serviceName,
    serviceID: c.serviceID,
    consultationId: c.consultationId,
    patientId: c.patientId,
    transcription: c.transcription,
    patientConsent: c.patientConsent,
    notes: c.notes,
    _id: c._id,
    template: c.template,
    templateId: c.templateId,
    templateName: c.templateName,
    shortSummary: c.shortSummary,
    pharmacyID: c.pharmacyID,
    preConsultationTemplate: c.preConsultationTemplate,
    createdByName: c.createdByName,
    createdBy: c.createdBy,
    createdOn: c.createdOn,
    completedOn: c.completedOn,
    consultationSummary: c.consultationSummary,
    consultationStatus: c.consultationStatus,
    consultationChat: c.consultationChat,
    consultationButtonStatus: c.consultationButtonStatus,
    companyID: c.companyID,
  }));
}

export function useConsultationsState() {
  const { patientState, dispatch } = useTitanApp();
  const {
    patientConsultations,
    showPatientConsultationsLoader,
    patientID,
    consultationAuditTimeline,
    patientActiveConsultation,
    consultationPoints,
    isConsultationPointsLoading,
    selectedConsultation,
  } = patientState || {};

  const [selectedRange, setSelectedRange] = useState<null | number | "all">(
    null,
  );
  const [localConsultations, setLocalConsultations] = useState<any[]>([]);
  const [localConsultationPoints, setLocalConsultationPoints] =
    useState<any>(null);

  // Sync with Redux state and reset when consultations change
  useEffect(() => {
    if (patientConsultations) {
      setLocalConsultations(patientConsultations);
    } else {
      setLocalConsultations([]);
    }
  }, [patientConsultations]);

  // Sync consultation points
  useEffect(() => {
    if (consultationPoints) {
      setLocalConsultationPoints(consultationPoints);
    } else {
      setLocalConsultationPoints(null);
    }
  }, [consultationPoints]);

  // ✅ ADD THIS useEffect (around line 320)
  React.useEffect(() => {
    if (patientConsultations) {
      setLocalConsultations(patientConsultations);
    } else {
      setLocalConsultations([]);
    }
  }, [patientConsultations]);
  // Parse consultation bullet points with error handling
  const consultationBulletPoints = useMemo(() => {
    try {
      if (!localConsultationPoints) return null;
      const parsed = JSON.parse(localConsultationPoints);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : null;
    } catch {
      return null;
    }
  }, [localConsultationPoints]);

  // Active consultations with proper filtering
  const activeConsultations = useMemo(() => {
    if (!localConsultations?.length) return [];

    let result = [...localConsultations];

    // ✅ filter only valid consultations
    result = result.filter((c) => {
      const isCancelled =
        c.consultationButtonStatus === ConsultationButtonStatus.Cancel;
      return (
        (c.consultationStatus === 2 || c.consultationStatus === 3) &&
        !isCancelled
      );
    });

    // ✅ apply date filter
    if (selectedRange && selectedRange !== "all") {
      const daysAgo = dayjs().subtract(selectedRange, "day");
      result = result.filter((c) => dayjs(c?.createdOn).isAfter(daysAgo));
    }

    // ✅ sort by most recent
    return result.sort(
      (a, b) =>
        new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(),
    );
  }, [localConsultations, selectedRange]);

  // Map consultations to UI format
  const consultationsUI = useMemo(
    () => mapPatientConsultationsToUI(localConsultations || []),
    [localConsultations],
  );

  // Reset consultation state
  const resetConsultationsState = useCallback(() => {
    setLocalConsultations([]);
    setLocalConsultationPoints(null);
    setSelectedRange(null);

    // Dispatch Redux actions if available
    if (dispatch) {
      dispatch({ type: "patient/clearConsultationsState" });
    }
  }, [dispatch]);

  // Enhanced summarize function with cleanup
  const summarizeWithCleanup = useCallback(
    async (data) => {
      try {
        // Clear previous summary
        setLocalConsultationPoints(null);

        // Dispatch Redux cleanup if available
        if (dispatch) {
          dispatch({ type: "patient/resetSelectedConsultation" });
        }

        const result = await summarizeConsultationSummaries(data);

        // If we have a patient ID, refresh consultations
        if (data?.patientId) {
          await getPatientConsultations(data.patientId);
        }

        return result;
      } catch (error) {
        console.error("Error summarizing consultations:", error);
        setLocalConsultationPoints(null);
        return null;
      }
    },
    [dispatch],
  );

  // Set selected consultation with cleanup
  const setSelectedConsultationWithCleanup = useCallback((consultation) => {
    // Clear previous points when selecting new consultation
    setLocalConsultationPoints(null);
    setSelectedConsultation(consultation);
  }, []);

  return {
    activeConsultations,
    selectedRange,
    setSelectedRange,
    showPatientConsultationsLoader,
    consultationsUI,
    setStreamedText,
    setSnomedCodes,
    patientActiveConsultation,
    summarizeConsultationSummaries: summarizeWithCleanup,
    consultationBulletPoints,
    isConsultationPointsLoading,
    setSelectedConsultation: setSelectedConsultationWithCleanup,
    selectedConsultation,
    resetConsultationsState,
  };
}

// Delete consultation function with proper cleanup
export async function deleteConsultation(
  consultationId: string,
  patientID: string,
  companyID: string,
  currentPharmacyId: string,
) {
  try {
    const data = {
      consultationId: consultationId,
      buttonStatus: ConsultationButtonStatus.Cancel,
    };

    await updateConsultationButtonStatus(data);

    await trackServiceBilling({
      companyID,
      consultationId,
      pharmacyId: currentPharmacyId,
      actiontype: "consultationStatus",
      consultationActionStatus: 5,
    });

    if (patientID) {
      // Fetch fresh data after deletion
      await Promise.all([
        getPatientConsultations(patientID),
        getPatientActiveConsultation(patientID),
      ]);
    }
  } catch (error) {
    console.error("❌ Error deleting consultation:", error);
    setConsultationLoadError(true);
  }
}

// Fetch consultation timeline
export async function fetchConsultationTimeline(consultationId: string) {
  try {
    if (!consultationId) return;
    await getConsultationAuditTimeline(consultationId);
  } catch (error) {
    console.error("❌ Error fetching consultation timeline:", error);
  }
}
